import { CoordPair } from 'h3-js'
import { z } from 'zod/v4'

import type { Feature, FeatureCollection, Polygon } from 'geojson'

export type AggregatedDemographics = {
	averages: Record<string, number>
	sortedCellIds: Record<string, string[]>
	cells: Record<string, HoneygridAPIH3CellProperties>
}

export type BoostConfig = {
	percentage: number
}

export type GeoJSONConversionResult<T> = {
	h3Cells: {
		h3Index: string
		blockPercentageOverlap: number
		cellPercentageOverlap: number
	}[]
	averageConversionTime: number | null
	metadata: T
}

export const CensusBlockValidator = z.object({
	type: z.literal('Feature'),
	properties: z.object({
		STATEFP20: z.string(),
		COUNTYFP20: z.string(),
		TRACTCE20: z.string(),
		BLOCKCE20: z.string(),
		GEOID20: z.string(),
		NAME20: z.string(),
		MTFCC20: z.string(),
		FUNCSTAT20: z.string(),
		ALAND20: z.number(),
		AWATER20: z.number(),
		INTPTLAT20: z.string(),
		INTPTLON20: z.string(),
		HOUSING20: z.number().nullable(),
		POP20: z.number().nullable(),
		UR20: z.string(),
		UACE20: z.string().nullable(),
		UATYPE20: z.string().nullable(),
	}),
	geometry: z.any(),
})

export type CensusBlock = z.infer<typeof CensusBlockValidator>

export const CensusBlockDataValidator = z.object({
	type: z.literal('FeatureCollection'),
	name: z.string(),
	crs: z.object({
		type: z.literal('name'),
		properties: z.object({
			name: z.string(),
		}),
	}),
	features: z.array(CensusBlockValidator),
})

export type CensusBlockData = z.infer<typeof CensusBlockDataValidator>

const USPSBaseDemographics = {
	zipCode: z.string(),
	cRRT: z.string(),
	geoCode: z.string(),
	state: z.string(),
	cityName: z.string(),
	postalCountyName: z.string(),
}

const USPSRouteMetrics = {
	distanceinMilesToCenterofCarrierRoute: z.number(),
	driveTimeinMinutestoCenterofCarrierRoute: z.number(),
}

const USPSUnitCounts = {
	sFDU: z.number(),
	mFDU: z.number(),
	names: z.number(),
}

export const USPSCarrierRoutePolygonValidator = z.object({
	geoCode: z.string(),
	geometry: z.any(),
	hasArea: z.literal(true),
	properties: z.lazy(() => USPSDemographicValidator),
})

export const USPSCarrierRoutePointValidator = z.object({
	geoCode: z.string(),
	geometry: z.any(),
	hasArea: z.literal(false),
	properties: z.lazy(() => USPSDemographicValidator),
})

export type USPSCarrierRoutePolygon = z.infer<typeof USPSCarrierRoutePolygonValidator>
export type USPSCarrierRoutePoint = z.infer<typeof USPSCarrierRoutePointValidator>

export type USPSCarrierRoute = USPSCarrierRoutePolygon | USPSCarrierRoutePoint

export const USPSCSVDemographicValidator = z.object({
	...USPSBaseDemographics,
	...USPSUnitCounts,
	...USPSRouteMetrics,
	businesses: z.number(),
	total: z.number(),
	runningTotal: z.number(),
	homeValue: z.number(),
	income: z.number(),
	age: z.number(),
	percentOwnerOccupied: z.number(),
	percentHouseholdWithChildren: z.number(),
	percentHispanic: z.number(),
	percentSaturation: z.number(),
	possibleTotal: z.number(),
	possibleResidential: z.number(),
})

export type USPSCSVDemographic = z.infer<typeof USPSCSVDemographicValidator>

export const USPSCSVNewMoversDemographicValidator = z.object({
	...USPSBaseDemographics,
	...USPSUnitCounts,
	...USPSRouteMetrics,
	phones: z.number(),
	dNCs: z.number(),
	mailedToTotal: z.number(),
	distanceinMilesToCenterofZipCode: z.number(),
	medianHomeValue: z.number(),
	medianIncome: z.number(),
	medianAge: z.number(),
	runningTotal: z.number(),
})

export type USPSCSVNewMoversDemographic = z.infer<typeof USPSCSVNewMoversDemographicValidator>

export const USPSDemographicValidator = USPSCSVDemographicValidator.extend({
	newMoverTotal: z.number(),
	newMoverSFDU: z.number(),
	newMoverMFDU: z.number(),
	newMoverNames: z.number(),
})

export type USPSDemographic = z.infer<typeof USPSDemographicValidator>

export type H3CellIntermediates<T> = {
	blocks: {
		[geoId: string]: {
			blockPercentageOverlap: number
			cellPercentageOverlap: number
		}
	}
	properties: {
		[K in keyof T]: {
			geoId: string
			value: T[K]
		}[]
	}
}

export type H3CellEntry<Properties> = {
	h3CellId: string
	properties: Properties
	intermediates: H3CellIntermediates<Properties>
}

export type IndexerPipelineProperty<Source, Properties, Key extends keyof Properties> = {
	processor: (args: {
		geoId: string
		properties: Source
		previous?: { geoId: string; value: Properties[Key] }[]
	}) => {
		geoId: string
		value: Properties[Key]
	}[]
	aggregator: ({
		values,
	}: {
		values: {
			value: Properties[Key]
			blockPercentageOverlap: number
			cellPercentageOverlap: number
		}[]
	}) => Properties[Key]
}

export const USPSPropertiesValidator = z.object({
	households: z.number().nullable(),
	homeValues: z.number().nullable(),
	income: z.number().nullable(),
	newMoverTotal: z.number().nullable(),
	newMoverSFDU: z.number().nullable(),
	newMoverMFDU: z.number().nullable(),
	sfdu: z.number().nullable(),
	mfdu: z.number().nullable(),
})

export type USPSProperties = z.infer<typeof USPSPropertiesValidator>

export const D1HoneygridCellRowValidator = z.object({
	id: z.string(),
	households: z.number().nullable(),
	sfdu: z.number().nullable(),
	mfdu: z.number().nullable(),
	new_mover_total: z.number().nullable(),
	new_mover_sfdu: z.number().nullable(),
	new_mover_mfdu: z.number().nullable(),
	income: z.number().nullable(),
	home_value: z.number().nullable(),
})

export type D1HoneygridCellRow = z.infer<typeof D1HoneygridCellRowValidator>

export type USPSH3CellEntry = H3CellEntry<USPSProperties>

export type USPSIndexerPipelineProperty<Key extends keyof USPSProperties> = IndexerPipelineProperty<
	USPSCarrierRoute['properties'],
	USPSProperties,
	Key
>

export type CustomerDataProperties = {
	customerCount?: number | null
	customerSales?: number | null
	customerMarketShare?: number | null
}

export type HoneygridCellData = USPSProperties & { [key: string]: any }

export type HoneygridCellMetadata = {
	cities: string[]
	zipCodes: string[]
}

export type HoneygridCellProperties = {
	data: HoneygridCellData
	metadata: HoneygridCellMetadata
}

export type CombinedHoneygridCell = {
	properties: HoneygridCellProperties
}

export type MaterializedFeatureCollection = FeatureCollection<
	Polygon,
	HoneygridCellData & {
		id: string
		center: CoordPair
	}
>

export type MaterializedGrid = {
	geojson: MaterializedFeatureCollection
	aggregatedDemographics: AggregatedDemographics
	fieldMappings?: import('./validators-and-types/DataSourceTypes').DataSourceFieldMappings
}

export type HoneygridAPIH3CellProperties = USPSProperties &
	CustomerDataProperties & {
		id: string
		center: [number, number]
		[k: string]: any
	}

export type HoneyGridFeatureCollection = FeatureCollection<Polygon, HoneygridAPIH3CellProperties>

export type HoneyGridFeature = Feature<Polygon, HoneygridAPIH3CellProperties>

export type HoneygridH3CellEntry = H3CellEntry<USPSProperties>

export enum ReferenceFields {
	CustomerCount = 'customerCount',
	CustomerSales = 'customerSales',
	CustomerMarketShare = 'customerMarketShare',
	Households = 'households',
	HomeValues = 'homeValues',
	Income = 'income',
	NewMoverTotal = 'newMoverTotal',
	NewMoverSFDU = 'newMoverSFDU',
	NewMoverMFDU = 'newMoverMFDU',
	/**
	 * Single-family dwelling units
	 */
	SFDU = 'sfdu',
	/**
	 * Multi-family dwelling units
	 */
	MFDU = 'mfdu',
}
